import { useState, useEffect } from 'react';
import { Boxes, X, AlertTriangle } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createArea, updateArea } from '../../api/areas';
import type { Area, FonteCer } from '../../api/areas';
import { getCerPerTipoArea, type CerPerTipo } from '../../api/settings';
import i18n from '../../i18n';

// Riga selezionata: id + fonte (chiave) + cer/descr (per la resa, anche fuori elenco).
type SelRow = { id: string; fonte: FonteCer; cer: string; nome_descrittivo: string | null };
const _key = (r: { id: string; fonte: string }) => `${r.fonte}:${r.id}`;

const schema = z.object({
  codice: z.string().min(1, i18n.t('aree.modal.val_codice')).max(10),
  descrizione: z.string().min(1, i18n.t('aree.modal.val_descrizione')),
  categoria: z.enum(['R13', 'EoW', 'DT', 'RP'] as const),
  capacita_kg: z.coerce.number().int().positive(i18n.t('aree.modal.val_capacita')),
  attiva: z.boolean(),
});
type FormData = z.infer<typeof schema>;

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';
const selectCls = `${inputCls} cursor-pointer`;

interface Props {
  area?: Area;
  onClose: () => void;
  onSaved: (area: Area) => void;
}

export default function AreaModal({ area, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Map<string, SelRow>>(
    () => new Map(
      (area?.materiali_ammessi ?? []).map((m) => [
        _key(m),
        { id: m.id, fonte: m.fonte, cer: m.cer, nome_descrittivo: m.nome_descrittivo },
      ]),
    ),
  );
  const [available, setAvailable] = useState<CerPerTipo[]>([]);

  const { register, handleSubmit, control, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      codice: area?.codice ?? '',
      descrizione: area?.descrizione ?? '',
      categoria: area?.categoria ?? 'R13',
      capacita_kg: area?.capacita_kg ?? 0,
      attiva: area?.attiva ?? true,
    },
  });

  // La sorgente CER dipende dalla tipologia area: ricarica al cambio.
  const categoria = useWatch({ control, name: 'categoria' });
  useEffect(() => {
    if (!categoria) return;
    void (async () => {
      try { setAvailable(await getCerPerTipoArea(categoria)); }
      catch { setAvailable([]); }
    })();
  }, [categoria]);

  function toggleRow(r: SelRow) {
    setSelected((prev) => {
      const next = new Map(prev);
      const k = _key(r);
      if (next.has(k)) next.delete(k);
      else next.set(k, r);
      return next;
    });
  }

  // V48: selezione PER RIGA (materiale). Righe selezionate non più nella sorgente
  // del tipo (es. dopo un cambio categoria) → mostrate come badge rimovibili.
  const availableKeys = new Set(available.map(_key));
  const outOfList = [...selected.values()].filter((r) => !availableKeys.has(_key(r)));

  async function onSubmit(data: unknown) {
    const d = data as FormData;
    setServerError(null);
    try {
      const materiali_ammessi = [...selected.values()].map((r) => ({ id: r.id, fonte: r.fonte }));
      const payload = { ...d, materiali_ammessi, capacita_kg: Number(d.capacita_kg) };
      let saved: Area;
      if (area) {
        saved = await updateArea(area.id, payload);
      } else {
        saved = await createArea(payload);
      }
      onSaved(saved);
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { detail?: string } } })?.response;
      if (resp?.status === 409) {
        setServerError(t('aree.modal.codice_409', { detail: (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? t('aree.modal.codice_esistente') }));
      } else {
        setServerError(resp?.data?.detail ?? t('aree.modal.errore_salvataggio'));
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark flex items-center gap-2">
            <Boxes size={16} className="text-sage" />
            {area ? t('aree.modal.titolo_modifica') : t('aree.modal.titolo_nuova')}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-ink-soft mb-1">{t('aree.modal.codice')}</label>
                <input type="text" className={inputCls} placeholder={t('aree.modal.codice_placeholder')} {...register('codice')} />
                {errors.codice && <p className="text-xs text-accent-danger mt-1">{errors.codice.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-soft mb-1">{t('aree.modal.categoria')}</label>
                <select className={selectCls} {...register('categoria')}>
                  <option value="R13">{t('aree.modal.cat_r13')}</option>
                  <option value="EoW">{t('aree.modal.cat_eow')}</option>
                  <option value="DT">{t('aree.modal.cat_dt')}</option>
                  <option value="RP">{t('aree.modal.cat_rp')}</option>
                </select>
                {errors.categoria && <p className="text-xs text-accent-danger mt-1">{errors.categoria.message}</p>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('aree.modal.descrizione')}</label>
              <input type="text" className={inputCls} placeholder={t('aree.modal.descrizione_placeholder')} {...register('descrizione')} />
              {errors.descrizione && <p className="text-xs text-accent-danger mt-1">{errors.descrizione.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('aree.modal.capacita')}</label>
              <input type="number" step="0.01" className={inputCls} placeholder="0" {...register('capacita_kg')} />
              {errors.capacita_kg && <p className="text-xs text-accent-danger mt-1">{errors.capacita_kg.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">
                {t('aree.modal.materiali')} <span className="text-ink-faint">{t('aree.modal.tipologia', { categoria })}</span>
              </label>
              {/* Righe (materiali) dalla sorgente del tipo: toggle per riga (id+fonte) */}
              {available.length === 0 ? (
                <p className="text-xs text-ink-soft py-1">{t('aree.modal.no_cer')}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {available.map((o) => {
                    const row: SelRow = { id: o.id, fonte: o.fonte, cer: o.cer, nome_descrittivo: o.nome_descrittivo };
                    const sel = selected.has(_key(row));
                    const label = o.nome_descrittivo ? `${o.cer} — ${o.nome_descrittivo}` : o.cer;
                    return (
                      <button
                        key={_key(row)}
                        type="button"
                        onClick={() => toggleRow(row)}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                          sel
                            ? 'bg-sage text-white border-sage'
                            : 'bg-white text-ink-mid border-stone-border hover:border-sage'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Righe selezionate fuori elenco per questa tipologia: badge rimovibili */}
              {outOfList.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-ink-soft mb-1">{t('aree.modal.fuori_elenco')}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {outOfList.map((r) => (
                      <span key={_key(r)} className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded-full">
                        <AlertTriangle size={11} /> {r.nome_descrittivo ? `${r.cer} — ${r.nome_descrittivo}` : r.cer}
                        <button type="button" onClick={() => toggleRow(r)} className="hover:text-accent-danger" aria-label={t('aree.modal.rimuovi')}>
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" id="attiva" className="w-4 h-4 text-sage rounded border-stone-border" {...register('attiva')} />
              <label htmlFor="attiva" className="text-sm text-ink-mid">{t('aree.modal.area_attiva')}</label>
            </div>

            {serverError && (
              <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">{serverError}</div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">{t('common.azioni.annulla')}</button>
            <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60">
              {isSubmitting ? t('aree.modal.salvataggio') : (area ? t('aree.modal.salva_modifiche') : t('aree.modal.crea'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
